import { Component, OnDestroy } from '@angular/core';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { Store, select } from '@ngrx/store';
import { Observable, BehaviorSubject, combineLatest } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { Tranche } from '../../../gameday/data/gameDay-data.transformations';
import { GameDayCalculatedData } from '../../../gameday/data/gameDay-data.calculation';
import { GameDayViewType } from '../../../state/data/state';
import { MonitorService } from '../../services/monitor.service';
import { LabelGenerator, ItemGroup } from '../../models/label';
import { MonitorFilter, MonitorViewType } from '../../models/monitor';
import {
  getFilterItemGroups,
  getTabIndexByViewType,
  overviewLabelGenerator,
  priorityLabelGenerator,
  retailLabelGenerator,
  VIEW_TYPES_BY_TAB_INDEX_MAPPING,
} from '../../mappers/monitor.calculations';
import { GameDayRootReducerState } from '../../../state/root.state';
import { getGamedayLoadingState } from '../../../state/data/reducer';

const TRANSITION_DURATION = 150;
const SHOWING_OVERVIEW = 'Showing Orders:';
const SHOWING_RETAIL = 'Showing Retail Orders:';
const SHOWING_PRIORITY = 'Showing Priority Orders:';
const ALL_OVERVIEW = 'All';
const ALL_RETAIL = 'All Retail';
const ALL_PRIORITY = 'All Priority';
const RETAIL_TAB_DISABLED_TOOL_TIP_TEXT =
  'There are no Retail Categories configured for this deal.';
const PRIORITY_TAB_DISABLED_TOOL_TIP_TEXT =
  'Priority tab is unavailable for sole-managed deals';

@Component({
  selector: 'muni-gameday-monitor',
  styleUrls: ['./monitor.styles.scss'],
  templateUrl: './monitor.template.html',
  providers: [MonitorService],
  animations: [
    trigger('enterAnimation', [
      state('false', style({ transform: 'translateY(100%)' })),
      state('true', style({ transform: 'translateY(0)' })),
      transition('true <=> false', animate(TRANSITION_DURATION)),
    ]),
    trigger('resizeAnimation', [
      state('true', style({ height: '100%' })),
      state('false', style({ height: 'calc(100% - 309px)' })),
      transition('true <=> false', animate(TRANSITION_DURATION)),
    ]),
  ],
})
export class MonitorComponent implements OnDestroy {
  public readonly loading: Observable<boolean> = this.store.pipe(
    select(getGamedayLoadingState)
  );

  public readonly viewType: Observable<MonitorViewType> = this.monitorService
    .viewType;

  public readonly gameDayViewType: Observable<GameDayViewType> = this
    .monitorService.gameDayViewTypeByMonitorViewType;

  public readonly isRetailTabDisabled: BehaviorSubject<boolean> = new BehaviorSubject(
    false
  );

  public selectedMaturityId: Observable<string> = this.monitorService
    .selectedMaturityId;

  public selectedTranche: Observable<Tranche> = this.monitorService.getTranche();

  public readonly activeTab: Observable<number> = combineLatest(
    this.viewType,
    this.isRetailTabDisabled
  ).pipe(
    map(([viewType, isRetailTabDisabled]: [MonitorViewType, boolean]) =>
      getTabIndexByViewType(viewType, isRetailTabDisabled)
    ),
    distinctUntilChanged()
  );

  public readonly filterItemGroups: Observable<
    ItemGroup[]
  > = this.monitorService.viewType.pipe(map(getFilterItemGroups));

  public readonly filterValues: Observable<MonitorFilter> = this.monitorService
    .filterValues;

  public readonly isSoleManagedDeal: Observable<boolean> = this.monitorService
    .isSoleManagedDeal;

  public retailPageData: Observable<GameDayCalculatedData> = this.monitorService
    .retailData;
  public priorityPageData: Observable<GameDayCalculatedData> = this
    .monitorService.priorityData;
  public overviewPageData: Observable<GameDayCalculatedData> = this
    .monitorService.data;

  public labelGenerator: Observable<LabelGenerator> = this.monitorService.viewType.pipe(
    map((viewType) => {
      switch (viewType) {
        case MonitorViewType.Overview:
          return overviewLabelGenerator;
        case MonitorViewType.Retail:
          return retailLabelGenerator;
        case MonitorViewType.Priority:
          return priorityLabelGenerator;
        default:
          throw new Error(`Unknown viewType = ${viewType}`);
      }
    })
  );

  public allLabel: Observable<string> = this.monitorService.viewType.pipe(
    map((viewType) => {
      switch (viewType) {
        case MonitorViewType.Overview:
          return ALL_OVERVIEW;
        case MonitorViewType.Retail:
          return ALL_RETAIL;
        case MonitorViewType.Priority:
          return ALL_PRIORITY;
        default:
          throw new Error(`Unknown viewType = ${viewType}`);
      }
    })
  );

  public showingLabel: Observable<string> = this.monitorService.viewType.pipe(
    map((viewType) => {
      switch (viewType) {
        case MonitorViewType.Overview:
          return SHOWING_OVERVIEW;
        case MonitorViewType.Retail:
          return SHOWING_RETAIL;
        case MonitorViewType.Priority:
          return SHOWING_PRIORITY;
        default:
          throw new Error(`Unknown viewType = ${viewType}`);
      }
    })
  );

  public highlightAll: Observable<boolean> = this.monitorService.viewType.pipe(
    map((viewType) => viewType !== MonitorViewType.Overview)
  );

  constructor(
    private store: Store<GameDayRootReducerState>,
    private monitorService: MonitorService
  ) {}

  public ngOnDestroy(): void {
    this.monitorService.selectMaturity(undefined);
    this.monitorService.resetViewTypes();
  }

  public onTabChange({ payload }): void {
    const viewType = VIEW_TYPES_BY_TAB_INDEX_MAPPING[payload];
    this.monitorService.setViewType(viewType);
  }

  public onFilterChange(changes: MonitorFilter): void {
    this.monitorService.setFilter(changes);
  }

  public disable(event): void {
    this.isRetailTabDisabled.next(event.payload);
  }

  public selectMaturity(event): void {
    const id = event ? event.id : event;
    this.monitorService.selectMaturity(id);
  }

  public getRetailTabTooltipText(isRetailTabDisabled: boolean): string {
    return isRetailTabDisabled ? RETAIL_TAB_DISABLED_TOOL_TIP_TEXT : '';
  }

  public getPriorityTabTooltipText(isSoloManagedDeal: boolean): string {
    return isSoloManagedDeal ? PRIORITY_TAB_DISABLED_TOOL_TIP_TEXT : '';
  }
}
